import React, { useState, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useUser } from './contexts/UserContext'
import { Toaster } from 'react-hot-toast'
import Header from './components/Header'
import Sidebar from './components/Sidebar'
import ChatPanel from './components/ChatPanel'
import FloatingAssistantButton from './components/FloatingAssistantButton'
import { UserProvider } from './contexts/UserContext'
import { DesignSessionProvider } from './contexts/DesignSessionContext'
import { FavoritesProvider } from './contexts/FavoritesContext'
import AuthModal from './components/AuthModal'
import BrandLogo from './components/BrandLogo'
import MaintenanceScreen from './components/MaintenanceScreen'
import maintenanceConfig from './config/maintenance.json'
import { isAdminUser } from './utils/userProfileFields'
import PremiumModal from './components/PremiumModal'
import FavoritesModal from './components/FavoritesModal'
import AppErrorBoundary from './components/AppErrorBoundary'
import VerifyEmailBanner from './components/VerifyEmailBanner'
import { lazyWithReload as lazy } from './utils/chunkReload'

const HomePreview = lazy(() => import('./components/HomePreview'))
const LegalPage = lazy(() => import('./components/LegalPage'))
const ExploreDesigns = lazy(() => import('./components/ExploreDesigns'))
const ClothingSearch = lazy(() => import('./components/ClothingSearch'))
const CustomAIDesigner = lazy(() => import('./components/CustomAIDesigner'))
const DesignStudio = lazy(() => import('./components/DesignStudio'))
const SettingsPage = lazy(() => import('./components/SettingsPage'))
const OktaCallback = lazy(() => import('./components/OktaCallback'))
const OrderCheckoutSuccess = lazy(() => import('./components/OrderCheckoutSuccess'))
const OrderCheckoutCancel = lazy(() => import('./components/OrderCheckoutCancel'))
const MyDesigns = lazy(() => import('./components/MyDesigns'))
const MyCustomRequests = lazy(() => import('./components/MyCustomRequests'))
const AdminGelatoOrders = lazy(() => import('./components/AdminGelatoOrders'))
const AdminCustomQuotes = lazy(() => import('./components/AdminCustomQuotes'))
const AdminTopGenerators = lazy(() => import('./components/AdminTopGenerators'))
const TierSelection = lazy(() => import('./components/TierSelection'))
const BlankCatalogBrowse = lazy(() => import('./components/BlankCatalogBrowse'))
const NotFoundPage = lazy(() => import('./components/NotFoundPage'))

const MAINTENANCE_MODE = maintenanceConfig.MAINTENANCE_MODE === true

const PageLoader = ({ dark = true }) => (
  <div className={`min-h-[50vh] flex flex-col items-center justify-center gap-4 ${dark ? 'app-dark' : 'bg-ink'}`}>
    <BrandLogo size="loading" />
    <p className="text-sm uppercase tracking-[0.2em] text-ivory-muted">Loading…</p>
  </div>
)

// Create a context to share sidebar state
export const SidebarContext = React.createContext()

// Old /custom-ai links redirect to /design, keeping query params (e.g. remix)
function RedirectToDesign() {
  const location = useLocation()
  return <Navigate to={{ pathname: '/design', search: location.search }} replace />
}

function AdminRoute({ children }) {
  const { user } = useUser()
  return isAdminUser(user) ? children : <NotFoundPage />
}

// Bumps when a signed-in account signs out or is replaced, so pages and the assistant
// remount without that account's component state. Signing in from a guest session keeps it.
function useAccountEpoch(uid) {
  const [account, setAccount] = useState({ uid, epoch: 0 })
  if (account.uid !== uid) {
    setAccount({ uid, epoch: account.uid ? account.epoch + 1 : account.epoch })
  }
  return account.epoch
}

function AppShell({
  isChatOpen, setIsChatOpen,
  isSidebarCollapsed, isMobileMenuOpen,
  handleAuthClick, handlePremiumClick, handleFavoritesClick,
  isAuthModalOpen, setIsAuthModalOpen, authMode,
  isPremiumModalOpen, setIsPremiumModalOpen,
  isFavoritesModalOpen, setIsFavoritesModalOpen
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const { isLoading, user } = useUser()
  const accountEpoch = useAccountEpoch(user?.uid || null)
  const adminBypass =
    MAINTENANCE_MODE &&
    location.pathname.startsWith('/admin') &&
    isAdminUser(user)

  const handleGetStarted = () => {
    // STAGE 1: tier selection precedes design tools (budget → existing Studio).
    // Replace when leaving legacy /design so it cannot sit under /create in history.
    navigate('/create', { replace: location.pathname === '/design' })
  }

  // Standalone pages render full-bleed with their own nav (no app header/sidebar).
  // LandingPage.jsx is the previous homepage, kept unused for a fast rollback.
  const isLanding = ['/', '/privacy', '/terms'].includes(location.pathname)
  const isLegacyPreview = location.pathname === '/home-preview'
  const isBare = isLanding || isLegacyPreview
  const isStudio = location.pathname === '/studio'
  const isImmersive = isBare || location.pathname === '/explore' || isStudio
  const showsVerifyBanner = ['/design', '/studio', '/create', '/my-designs', '/my-custom-requests']
    .some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`))

  if (isLoading) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center gap-4 ${isBare ? 'bg-ink' : 'app-dark'}`}>
        <BrandLogo size="loading" />
        <p className="text-sm uppercase tracking-[0.2em] text-ivory-muted">Loading…</p>
      </div>
    )
  }

  if (MAINTENANCE_MODE && !adminBypass) {
    return <MaintenanceScreen />
  }

  return (
    <div className={`min-h-screen ${isBare ? 'bg-ink' : 'app-dark'}`}>
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#131313',
            color: '#f3efe7',
            border: '1px solid rgba(243, 239, 231, 0.14)',
            fontFamily: '"Instrument Sans", "Avenir Next", "Segoe UI", sans-serif',
            fontSize: '14px'
          },
          success: { duration: 3000, iconTheme: { primary: '#c9a96e', secondary: '#0c0c0c' } },
          error: { duration: 5000, iconTheme: { primary: '#e2a397', secondary: '#0c0c0c' } },
        }}
      />
      {!isBare && !isStudio && (
        <Header 
          onChatToggle={() => setIsChatOpen(!isChatOpen)} 
          onAuthClick={handleAuthClick}
          onPremiumClick={handlePremiumClick}
          onFavoritesClick={handleFavoritesClick}
        />
      )}
      <div className="flex">
        {!isBare && !isStudio && <Sidebar onPremiumClick={handlePremiumClick} onAuthClick={handleAuthClick} />}
        <main className={`flex-1 min-w-0 transition-all duration-300 ease-in-out ${
          isBare || isStudio ? '' : isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
        } ${isImmersive ? 'p-0' : 'p-4 sm:p-6'}`}>
          <AppErrorBoundary resetKey={location.pathname} fullScreen={isBare}>
          {showsVerifyBanner && (
            <div className={isStudio ? 'px-4 pt-4' : ''}>
              <VerifyEmailBanner />
            </div>
          )}
          <Suspense key={accountEpoch} fallback={<PageLoader dark={!isBare} />}>
          <Routes>
            <Route path="/login/callback" element={<OktaCallback />} />
            <Route path="/" element={<HomePreview onGetStarted={handleGetStarted} onAuthClick={handleAuthClick} />} />
            <Route path="/home-preview" element={<Navigate to="/" replace />} />
            <Route path="/privacy" element={<LegalPage type="privacy" />} />
            <Route path="/terms" element={<LegalPage type="terms" />} />
            <Route path="/explore" element={<ExploreDesigns onPremiumClick={handlePremiumClick} onAuthClick={handleAuthClick} />} />
            <Route path="/search" element={<ClothingSearch />} />
            {/* STAGE 1 entry: pick manufacturing tier before designing */}
            <Route path="/create/:tierId/blanks" element={<BlankCatalogBrowse />} />
            <Route path="/create" element={<TierSelection />} />
            {/* The AI designer is the one true Design page; old /custom-ai links redirect */}
            <Route path="/design" element={<CustomAIDesigner onPremiumClick={handlePremiumClick} onAuthClick={handleAuthClick} />} />
            <Route path="/my-designs" element={<MyDesigns />} />
            <Route path="/my-custom-requests" element={<MyCustomRequests />} />
            <Route path="/design-advanced" element={<Navigate to="/design" replace />} />
            <Route path="/custom-ai" element={<RedirectToDesign />} />
            <Route path="/studio" element={<DesignStudio onPremiumClick={handlePremiumClick} onAuthClick={handleAuthClick} />} />
            <Route path="/settings" element={<SettingsPage onPremiumClick={handlePremiumClick} onAuthClick={handleAuthClick} />} />
            <Route path="/order/success" element={<OrderCheckoutSuccess />} />
            <Route path="/order/cancel" element={<OrderCheckoutCancel />} />
            <Route path="/admin/gelato-orders" element={<AdminRoute><AdminGelatoOrders /></AdminRoute>} />
            <Route path="/admin/custom-quotes" element={<AdminRoute><AdminCustomQuotes /></AdminRoute>} />
            <Route path="/admin/top-generators" element={<AdminRoute><AdminTopGenerators /></AdminRoute>} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Suspense>
          </AppErrorBoundary>
        </main>
        {isChatOpen && (
          <div className="app-dark contents">
            <ChatPanel key={accountEpoch} onClose={() => setIsChatOpen(false)} onPremiumClick={handlePremiumClick} onAuthClick={handleAuthClick} />
          </div>
        )}
        {!isChatOpen && (isLanding || isStudio || location.pathname === '/explore') && (
          <FloatingAssistantButton onClick={() => setIsChatOpen(true)} />
        )}
      </div>
      <div className="app-dark contents">
        <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} initialMode={authMode} />
        <PremiumModal isOpen={isPremiumModalOpen} onClose={() => setIsPremiumModalOpen(false)} />
        <FavoritesModal isOpen={isFavoritesModalOpen} onClose={() => setIsFavoritesModalOpen(false)} />
      </div>
    </div>
  )
}

function App() {
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
  const [authMode, setAuthMode] = useState('signin')
  const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false)
  const [isFavoritesModalOpen, setIsFavoritesModalOpen] = useState(false)

  const handleAuthClick = (mode) => {
    setAuthMode(mode)
    setIsAuthModalOpen(true)
  }

  const handlePremiumClick = () => {
    setIsPremiumModalOpen(true)
  }

  const handleFavoritesClick = () => {
    setIsFavoritesModalOpen(true)
  }

  return (
    <UserProvider>
      <DesignSessionProvider>
      <FavoritesProvider>
        <SidebarContext.Provider value={{ isSidebarCollapsed, setIsSidebarCollapsed, isMobileMenuOpen, setIsMobileMenuOpen }}>
        <Router>
          <AppShell
            isChatOpen={isChatOpen}
            setIsChatOpen={setIsChatOpen}
            isSidebarCollapsed={isSidebarCollapsed}
            isMobileMenuOpen={isMobileMenuOpen}
            handleAuthClick={handleAuthClick}
            handlePremiumClick={handlePremiumClick}
            handleFavoritesClick={handleFavoritesClick}
            isAuthModalOpen={isAuthModalOpen}
            setIsAuthModalOpen={setIsAuthModalOpen}
            authMode={authMode}
            isPremiumModalOpen={isPremiumModalOpen}
            setIsPremiumModalOpen={setIsPremiumModalOpen}
            isFavoritesModalOpen={isFavoritesModalOpen}
            setIsFavoritesModalOpen={setIsFavoritesModalOpen}
          />
        </Router>
        </SidebarContext.Provider>
      </FavoritesProvider>
      </DesignSessionProvider>
    </UserProvider>
  )
}

export default App 